import { useIsMounted, useSyncedRef } from "@react-hookz/web";
import { useCallback } from "react";

type NavigatorWithConnection = Navigator & {
  // Connection isn't supported on Firefox / Safari, so may be undefined
  // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/connection
  connection?: {
    rtt: number;
  };
};

type DownloadFilesFn = (fileUrls?: string[]) => Promise<void>;

/**
 * Downloads a list of files, with a delay between each file to prevent the browser from cancelling the download.
 *
 * @param fileUrls A list of file URLs to download, these files can be provided to the hook directly or via the returned
 * function.
 */
export const useDownloadFiles = (fileUrls?: string[]): DownloadFilesFn => {
  const isMounted = useIsMounted();
  const filesUrlsRef = useSyncedRef(fileUrls || []);

  return useCallback(
    (overrideFileUrls?: string[]) => {
      // When downloading multiple files, we need to add a wait between each file.
      // If the download for the previous file hasn't yet started, then the next file will cause the previous file to be
      // cancelled (the download starts after the initial response from the server).
      const latencyEstimate = (window.navigator as NavigatorWithConnection).connection?.rtt || 500;
      const waitTime = 1000 + latencyEstimate;

      const urlsToDownload = overrideFileUrls || filesUrlsRef.current;

      return new Promise<void>((resolve) => {
        if (!urlsToDownload.length) {
          return resolve();
        }

        urlsToDownload.forEach((file, i) => {
          setTimeout(() => {
            if (!isMounted()) return;

            const a = document.createElement("a") as HTMLAnchorElement;
            a.href = file;
            a.download = "";

            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            if (i === urlsToDownload.length - 1) {
              resolve();
            }
          }, waitTime * i);
        });
      });
    },
    [filesUrlsRef, isMounted],
  );
};
